import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { EM_DASH } from 'libs.constants';
import { InlineNotification, ModalWindow, Spinner, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { getApiErrorMessage } from '../../helpers/api.helpers';
import { getStudyShortCode } from '../../helpers/study.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
import { EParticipantInviteStatus } from '../../types/participants.type';
import { UserCheckIcon } from '../svgIcons/user_check.icon';
import { resendParticipantInvite } from './helpers/participant_form.helpers';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
export const ParticipantInviteModal = ({ participant, onClose, onDone }) => {
    const translate = useLocalizeMessage();
    const org = useOrgContext();
    const site = useSiteContext();
    const queryClient = useQueryClient();
    const { environment } = useCurrentEnvironment();
    const { isUserPermitted } = useLumosAuth();
    const { addNotification } = useToastNotification();
    const [isSendingEmail, setIsSendingEmail, unsetIsSendingEmail] = useBooleanState(false);
    const [isLoading, setIsLoadingTrue, setIsLoadingFalse] = useBooleanState(participant.c_invite === EParticipantInviteStatus.EXPIRED);
    // Check if we're on R5.2+ (version 50200 or higher)
    const isR52OrHigher = org?.c_pinned_version !== undefined && org.c_pinned_version >= 50200;
    // Show warning only if:
    // - Org is NOT no-PII, AND
    // - Participant has no email, AND
    // - NOT on R5.2+ (because R5.2+ with no email means "setup without email" was used, follows NOPII flow)
    const shouldShowNoEmailWarning = !org?.c_no_pii && !participant?.c_email && !isR52OrHigher;
    const getStudyCode = async () => {
        if (!environment) {
            return null;
        }
        return await getStudyShortCode(environment);
    };
    const { data: studyCode } = useQuery({
        queryKey: ['getStudyCode', environment?.code],
        queryFn: getStudyCode,
        enabled: !queryClient.getQueryData(['getStudyCode', environment?.code]),
    });
    const resendInvite = async () => {
        if (!environment) {
            throw new Error('Environment is not defined');
        }
        const isResendForExpiredInvitation = participant.c_invite === EParticipantInviteStatus.EXPIRED;
        if (studyCode) {
            if (isResendForExpiredInvitation) {
                setIsLoadingTrue();
            }
            else {
                setIsSendingEmail();
            }
            try {
                await resendParticipantInvite({
                    environment,
                    org,
                    participant,
                    site,
                    studyInviteCode: studyCode,
                    sendInviteEmail: !isResendForExpiredInvitation,
                });
                if (!isResendForExpiredInvitation) {
                    addNotification({
                        title: '',
                        subtitle: translate('status.participant_invite.sent'),
                        type: 'success',
                    });
                }
            }
            catch (e) {
                console.error('Error sending participant invite email', e);
                const message = getApiErrorMessage(e);
                addNotification({
                    title: '',
                    subtitle: message ?? translate('error.generic'),
                    type: 'error',
                });
            }
            finally {
                if (isResendForExpiredInvitation) {
                    queryClient.invalidateQueries({
                        queryKey: ['participantDetails', participant._id],
                    });
                    setIsLoadingFalse();
                }
                else {
                    unsetIsSendingEmail();
                }
            }
        }
    };
    const sendingEmailButton = (_jsxs("div", { className: 'flex items-center gap-3', children: [_jsx(Spinner, { size: 'sm' }), " ", translate('status.participant_invite.sending_email'), "..."] }));
    useEffect(() => {
        if (participant.c_invite === EParticipantInviteStatus.EXPIRED) {
            resendInvite();
        }
    }, []);
    return (_jsx(ModalWindow, { isOpen: true, title: translate('generic.invite'), closeWindow: onClose, footerSecondaryActionButtons: [
            {
                label: translate('label.participant_invite.email_codes'),
                onClick: resendInvite,
                disabled: isSendingEmail || !participant?.c_email || isLoading,
                content: isSendingEmail ? sendingEmailButton : undefined,
                id: 'email-invite-codes',
                dataTestId: 'email-invite-codes',
                hidden: !isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_EMAIL_INVITE) || !participant?.c_email,
            },
            { label: translate('generic.cancel'), onClick: onClose },
        ], footerPrimaryActionButton: {
            label: translate('generic.done'),
            onClick: onDone ?? onClose,
            id: 'invite-modal-done',
            dataTestId: 'invite-modal-done',
            disabled: isSendingEmail || isLoading,
        }, width: 'full', children: isLoading ? (_jsx("div", { className: 'flex flex-col h-full items-center justify-center', children: _jsx(Spinner, { size: 'xl' }) })) : (_jsxs("div", { className: 'flex flex-col h-full justify-between', children: [_jsxs("div", { className: 'flex flex-col flex-grow gap-6 items-center justify-center', children: [_jsx(UserCheckIcon, { className: 'flex-1 nucleus-text-primary-500' }), _jsxs("section", { className: 'text-center', children: [_jsx("h4", { className: 'nucleus-text-h4 nucleus-text-text-primary font-extrabold', children: translate('generic.invite_information') }), _jsx("p", { className: 'text-sm nucleus-text-neutral-500', children: translate('generic.invite_code.share') })] }), _jsxs("section", { className: 'text-center', children: [_jsx("span", { children: translate('label.study_code') }), _jsx("div", { className: 'nucleus-px-6 py-1 bg-neutral-100', children: _jsx("span", { "data-testid": 'study-code', className: 'nucleus-text-h1 nucleus-font-light', children: studyCode ?? EM_DASH }) })] }), _jsxs("section", { className: 'text-center', children: [_jsx("span", { children: translate('label.invite_code') }), _jsx("div", { className: 'nucleus-px-6 py-1 bg-neutral-100', children: _jsx("span", { "data-testid": 'invite-code', className: 'nucleus-text-h1 nucleus-font-light', children: participant?.c_access_code ?? EM_DASH }) })] })] }), !!shouldShowNoEmailWarning && (_jsx("div", { className: 'flex', children: _jsx(InlineNotification, { subtitle: translate('info.participant_invite.email_required'), title: translate('info.participant_invite.no_email'), type: 'warning' }) }))] })) }));
};
